// src/components/stock/EsimInventoriesSearchPanel.tsx
//
// EsimInventoriesSearchPanel
// - Presentation-only search UI for eSIM Inventory page.
// - Delegates all state ownership to the parent page.
// - Uses shared SearchPanel for:
//   - Search / Reset buttons
//   - Optional autoSearch (pass-through only)
//   - Active chips (with readable labels)
//
// Layout:
// - Row 1: Stock Code, eSIM Type, eSIM Status
// - Row 2: Parameter Type, Parameter Keyword
// - Row 3: Expiry Date
//
// IMPORTANT (policy):
// - Do NOT hardcode autoSearch defaults here.
//   SearchPanel owns global defaults via constants.
//   This component must pass through `autoSearch` only if provided by the page.

import React, { useMemo } from 'react';
import { Autocomplete, Grid } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import {
  SearchPanel,
  SearchDateRange,
  SearchTextField,
  type ActiveChipsConfig,
  type SearchChipSpec,
  type SearchPanelAction,
  type SetSearchValueFn,
} from '@components/common/search';

import type { GetEsimInventoriesParams, EsimInventoryParameterType } from '@apis/esim';
import type { EsimStatus, EsimType } from '@models/stock';

type SearchForm = GetEsimInventoriesParams;

/**
 * NOTE:
 * - `expireAtFrom/expireAtTo` are controlled by native date inputs.
 * - The controlled value must be 'YYYY-MM-DD' (string) to render correctly.
 * - The page converts them into timestamps when calling APIs / syncing URL.
 */
type Option<T> = { label: string; value: T };

export type EsimInventoriesSearchPanelProps = {
  open: boolean;
  onOpenChange: (next: boolean) => void;

  // Draft state (owned by page)
  draftSearch: SearchForm;
  setValue: SetSearchValueFn<SearchForm>;

  // Options + selected values (owned by page)
  stockOptions: Array<Option<number>>;
  esimTypeOptions: Array<Option<EsimType>>;
  esimStatusOptions: Array<Option<EsimStatus>>;
  parameterTypeOptions: Array<Option<EsimInventoryParameterType>>;

  selectedStock: Option<number> | null;
  selectedEsimType: Option<EsimType> | null;
  selectedEsimStatus: Option<EsimStatus> | null;
  selectedParameterType: Option<EsimInventoryParameterType> | null;

  // Actions (owned by page)
  onSearch: () => void;
  onReset: () => void;

  // Export
  onExport: () => void;
  exportLoading?: boolean;
  exportDisabled?: boolean;

  // Pass-through only
  autoSearch?: { enabled?: boolean; delayMs?: number };
};

const EsimInventoriesSearchPanel: React.FC<EsimInventoriesSearchPanelProps> = ({
  open,
  onOpenChange,

  draftSearch,
  setValue,

  stockOptions,
  esimTypeOptions,
  esimStatusOptions,
  parameterTypeOptions,

  selectedStock,
  selectedEsimType,
  selectedEsimStatus,
  selectedParameterType,

  onSearch,
  onReset,

  onExport,
  exportLoading = false,
  exportDisabled = false,

  autoSearch,
}) => {
  /* ------------------------------------------------------------------ */
  /* Lookup maps (O(1) chip label access)                                */
  /* ------------------------------------------------------------------ */

  const stockLabelById = useMemo(() => {
    const map = new Map<number, string>();
    stockOptions.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [stockOptions]);

  const esimTypeLabelByValue = useMemo(() => {
    const map = new Map<EsimType, string>();
    esimTypeOptions.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [esimTypeOptions]);

  const esimStatusLabelByValue = useMemo(() => {
    const map = new Map<EsimStatus, string>();
    esimStatusOptions.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [esimStatusOptions]);

  const parameterTypeLabelByValue = useMemo(() => {
    const map = new Map<EsimInventoryParameterType, string>();
    parameterTypeOptions.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [parameterTypeOptions]);

  /* ------------------------------------------------------------------ */
  /* Chips                                                              */
  /* ------------------------------------------------------------------ */

  const chips: Array<SearchChipSpec<SearchForm>> = useMemo(
    () => [
      {
        key: 'stockId',
        label: 'Stock Code',
        isActive: (s) => typeof s.stockId === 'number',
        getValueText: (s) => {
          const id = s.stockId;
          if (typeof id !== 'number') return '';
          return stockLabelById.get(id) ?? String(id);
        },
        clear: ({ setValue }) => setValue('stockId', null),
      },
      {
        key: 'esimType',
        label: 'eSIM Type',
        isActive: (s) => Boolean(s.esimType),
        getValueText: (s) => {
          const v = s.esimType as EsimType | undefined;
          if (!v) return '';
          return esimTypeLabelByValue.get(v) ?? String(v);
        },
        clear: ({ setValue }) => setValue('esimType', null),
      },
      {
        key: 'esimStatus',
        label: 'eSIM Status',
        isActive: (s) => Boolean(s.esimStatus),
        getValueText: (s) => {
          const v = s.esimStatus as EsimStatus | undefined;
          if (!v) return '';
          return esimStatusLabelByValue.get(v) ?? String(v);
        },
        clear: ({ setValue }) => setValue('esimStatus', null),
      },
      {
        key: 'parameterType',
        label: 'Parameter Type',
        isActive: (s) => Boolean(s.parameterType),
        getValueText: (s) => {
          const v = s.parameterType as EsimInventoryParameterType | undefined;
          if (!v) return '';
          return parameterTypeLabelByValue.get(v) ?? String(v);
        },
        clear: ({ setValue }) => setValue('parameterType', null),
      },
      {
        key: 'parameterValue',
        label: 'Keyword',
        isActive: (s) => Boolean(String(s.parameterValue ?? '').trim()),
        getValueText: (s) => String(s.parameterValue ?? '').trim(),
        clear: ({ setValue }) => setValue('parameterValue', ''),
      },
      {
        key: 'expireAtFrom',
        label: 'Expiry From',
        isActive: (s) => Boolean(String(s.expireAtFrom ?? '').trim()),
        getValueText: (s) => String(s.expireAtFrom ?? '').trim(),
        clear: ({ setValue }) => setValue('expireAtFrom', null),
      },
      {
        key: 'expireAtTo',
        label: 'Expiry To',
        isActive: (s) => Boolean(String(s.expireAtTo ?? '').trim()),
        getValueText: (s) => String(s.expireAtTo ?? '').trim(),
        clear: ({ setValue }) => setValue('expireAtTo', null),
      },
    ],
    [esimStatusLabelByValue, esimTypeLabelByValue, parameterTypeLabelByValue, stockLabelById],
  );

  const chipsConfig: ActiveChipsConfig<SearchForm> = useMemo(
    () => ({
      enabled: true,
      search: draftSearch,
      setValue,
      chips,
      maxVisible: 2,
      onAfterClear: onSearch,
    }),
    [chips, draftSearch, onSearch, setValue],
  );

  /* ------------------------------------------------------------------ */
  /* Actions                                                            */
  /* ------------------------------------------------------------------ */

  const actions: SearchPanelAction[] = useMemo(
    () => [
      {
        key: 'export',
        label: 'Export',
        startIcon: <FileDownloadIcon />,
        variant: 'outlined',
        onClick: onExport,
        disabled: exportDisabled || exportLoading,
      },
    ],
    [exportDisabled, exportLoading, onExport],
  );

  /* ------------------------------------------------------------------ */
  /* Render                                                             */
  /* ------------------------------------------------------------------ */

  return (
    <SearchPanel
      title="Filters"
      open={open}
      onOpenChange={onOpenChange}
      onSearch={onSearch}
      onReset={onReset}
      chipsConfig={chipsConfig}
      actions={actions}
      autoSearch={autoSearch}
    >
      <Grid container spacing={2}>
        {/* Row 1 */}
        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete<Option<number>, false, false, false>
            disablePortal
            options={stockOptions}
            value={selectedStock}
            onChange={(_e, v) => setValue('stockId', (v?.value ?? null) as number | null)}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            renderInput={(params) => (
              <SearchTextField {...params} label="Stock Code" size="small" fullWidth onClear={() => setValue('stockId', null)} />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete<Option<EsimType>, false, false, false>
            disablePortal
            options={esimTypeOptions}
            value={selectedEsimType}
            onChange={(_e, v) => setValue('esimType', (v?.value ?? null) as EsimType | null)}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            renderInput={(params) => (
              <SearchTextField {...params} label="eSIM Type" size="small" fullWidth onClear={() => setValue('esimType', null)} />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete<Option<EsimStatus>, false, false, false>
            disablePortal
            options={esimStatusOptions}
            value={selectedEsimStatus}
            onChange={(_e, v) => setValue('esimStatus', (v?.value ?? null) as EsimStatus | null)}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            renderInput={(params) => (
              <SearchTextField {...params} label="eSIM Status" size="small" fullWidth onClear={() => setValue('esimStatus', null)} />
            )}
          />
        </Grid>

        {/* Row 2 */}
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete<Option<EsimInventoryParameterType>, false, false, false>
            disablePortal
            options={parameterTypeOptions}
            value={selectedParameterType}
            onChange={(_e, v) => setValue('parameterType', (v?.value ?? null) as EsimInventoryParameterType | null)}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            renderInput={(params) => (
              <SearchTextField {...params} label="Parameter Type" size="small" fullWidth onClear={() => setValue('parameterType', null)} />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={9}>
          <SearchTextField
            label="Parameter Keyword"
            placeholder="LPA_URL / REDEMPTION_CODE / ICCID / MSISDN"
            size="small"
            fullWidth
            value={String(draftSearch.parameterValue ?? '')}
            onChangeValue={(v) => setValue('parameterValue', v)}
            onClear={() => setValue('parameterValue', '')}
          />
        </Grid>

        {/* Row 3 */}
        <Grid item xs={12}>
          <SearchDateRange
            label="Expiry Date"
            from={draftSearch.expireAtFrom ? String(draftSearch.expireAtFrom) : ''}
            to={draftSearch.expireAtTo ? String(draftSearch.expireAtTo) : ''}
            onChangeFrom={(v) => setValue('expireAtFrom', v)}
            onChangeTo={(v) => setValue('expireAtTo', v)}
          />
        </Grid>
      </Grid>
    </SearchPanel>
  );
};

export default EsimInventoriesSearchPanel;
